import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react'
import {
    Box,
    Grid,
    MenuItem,
    IconButton,
    Typography,
    Button,
    Checkbox,
    CircularProgress
} from '@material-ui/core'
import { InputLabelStyled } from './AssigneeChipInputField'
import { translate } from 'components/shared/internationalization'
import { ClassNames } from '@emotion/react'
import { makeStyles } from '@material-ui/core/styles'
import { AtlasIcon } from 'web-components/atlas-icon'
import { ColorBaseDarkGray, FontWeightRegular, ColorBaseWhite } from 'assets/styles/variables'
import { Field, useForm } from 'react-final-form'
import { NewDialogTextField } from 'components/shared/StyledComponents'
import {
    DAYS_DEFAULT_VALUE,
    MAX_DAYS,
    MAX_MONTHS,
    MAX_REMINDERS,
    MAX_WEEKS,
    REMINDER_MIN_VALUE,
    REMINDER_OPTIONS
} from 'common/constants/constants'
import TextLengthCounter from 'components/shared/TextLengthCounter'
import i18n from 'common/util/internationalization'
import { validDateReminderDate } from 'common/formatters/date'
import {
    getDefaultActionItemReminders,
    convertDefaultRemindersToInternalFormat
} from 'common/util/defaultReminders'
import { format } from 'date-fns'
import RestrictedWarningIcon from 'assets/icons/RestrictedWarningIcon'
// Reminder type
type Reminder = {
    id: number
    label: string
    type: string
}

// Styles
const useStyles = makeStyles(() => ({
    reminderLabelClassActionDrawer: { marginTop: '1.5rem' },
    reminderLabelClass: { marginTop: '2.9rem' },
    reminderManageSection: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: FontWeightRegular,
        fontStyle: 'normal',
        lineHeight: '16px',
        paddingTop: '8px',
        color: ColorBaseDarkGray,
        marginBottom: '20px'
    },
    reminderManageSectionActionDrawer: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: FontWeightRegular,
        fontStyle: 'normal',
        lineHeight: '16px',
        paddingTop: '8px',
        color: ColorBaseDarkGray,
        marginBottom: '10px'
    },
    reminderAdditionalButton: {
        color: 'var(--Semantic-Color-Action-Default, #455D82)',
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: 'Source Sans Pro',
        lineHeight: '20px',
        paddingLeft: '12px',
        marginBottom: '8px',
        '&:focus': {
            outline: 'none',
            boxShadow: '0 0 0 1px #ffffff, 0 0 0 3px #455D82',
            borderRadius: '2px'
        }
    },
    reminderMessage: {
        color: 'var(--Semantic-Color-Type-Muted, #676767)',
        fontSize: '13px',
        fontFamily: 'Source Sans Pro',
        fontWeight: 400,
        lineHeight: '16px',
        paddingBottom: '9px',
        paddingTop: '6px'
    },
    textFieldContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        placeItems: 'center',
        marginBottom: '10px'
    },
    textFieldContainerActionDrawer: {
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        placeItems: 'center',
        marginTop: '5px'
    },
    textfiledcontainerActionDrawer: {
        marginBottom: '10px'
    },
    textField: { minHeight: 40 },
    reminderRowValue: {
        color: 'var(--Semantic-Color-Type-Default, #1E1E1E)',
        display: 'flex',
        alignItems: 'center',
        width: '9%',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400
    },
    reminderRowValueActionDrawer: {
        color: 'var(--Semantic-Color-Type-Default, #1E1E1E)',
        display: 'flex',
        alignItems: 'center',
        width: '14%',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400
    },
    reminderRowUnitOnDueDate: {
        color: 'var(--Semantic-Color-Type-Default, #1E1E1E)',
        display: 'flex',
        alignItems: 'center',
        width: '30.3%',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400,
        '& .MuiInputBase-input': {
            lineHeight: '14px'
        }
    },
    reminderRowUnitOnDueDateActionDrawer: {
        color: 'var(--Semantic-Color-Type-Default, #1E1E1E)',
        display: 'flex',
        alignItems: 'center',
        width: '46%',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400,
        '& .MuiInputBase-input': {
            lineHeight: '14px'
        }
    },
    reminderRowUnit: {
        color: 'var(--Semantic-Color-Type-Default, #1E1E1E)',
        display: 'flex',
        alignItems: 'center',
        width: '20%',
        marginLeft: '12px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400
    },
    reminderRowUnitActionDrawer: {
        color: 'var(--Semantic-Color-Type-Default, #1E1E1E)',
        display: 'flex',
        alignItems: 'center',
        width: '30%',
        marginLeft: '12px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400
    },
    reminderRowUnitClose: {
        display: 'flex',
        alignItems: 'center',
        width: '15%'
    },
    rightAlignInput: {
        '& input': {
            textAlign: 'right'
        }
    },
    checkBoxContainer: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '16px',
        display: 'flex',
        placeItems: 'center'
    },
    checkbox: {
        padding: '0px',
        marginRight: '8px',
        width: '24px',
        height: '24px',
        '&.Mui-checked': {
            color: '#2F3B4D'
        },
        '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)'
        },
        '&.Mui-focusVisible, &:active': {
            boxShadow: `0px 0px 0px 3px #0774EE, 0px 0px 0px 1px ${ColorBaseWhite}`,
            borderRadius: '4px'
        }
    },
    checkboxTitle: {
        fontFamily: 'Source Sans Pro',
        fontSize: '13px',
        fontWeight: 400,
        color: '#1E1E1E',
        cursor: 'pointer'
    },
    noSpinner: {
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0
        },
        '& input[type=number]': {
            MozAppearance: 'textfield'
        }
    },
    errorInput: {
        '& .MuiOutlinedInput-root': {
            color: '#AF292E',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Source Sans Pro',
            fontSize: 13,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '16px' /* 123.077% */,

            borderRadius: '4px',
            border: '1px solid #af292e',

            '&:hover': {
                border: '2px solid #af292e'
            },
            '&.Mui-focused': {
                border: '2px solid #af292e'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
            }
        }
    },
    errorMessage: {
        color: '#AF292E',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'Source Sans Pro',
        fontSize: 13,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '16px' /* 123.077% */
    },
    warningMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: 'var(--Semantic-Color-Status-Warning, #EAA14B)',
        fontFeatureSettings: "'liga' off, 'clig' off",
        fontFamily: 'Source Sans Pro',
        fontSize: 13,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '16px'
    },
    warningInput: {
        '& .MuiOutlinedInput-root': {
            color: 'var(--Semantic-Color-Status-Warning, #EAA14B)',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Source Sans Pro',
            fontSize: 13,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '16px',

            borderRadius: '4px',
            border: '1px solid #EAA14B',

            '&:hover': {
                border: '2px solid #EAA14B'
            },
            '&.Mui-focused': {
                border: '2px solid #EAA14B'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
            }
        }
    },
    disabledInput: {
        '& .MuiOutlinedInput-root': {
            '& input': {
                color: 'rgba(0, 0, 0, 0.38) !important'
            },
            '& .MuiSelect-select': {
                color: 'rgba(0, 0, 0, 0.38) !important'
            }
        }
    }
}))

interface Props {
    dueDate: string | Date | null
    actionSummary?: boolean
    initialReminders?: Array<{ reminder_lead_value: number; reminder_lead_unit: string }>
    initialRememberSettings?: boolean
    isLoadingRememberSettings?: boolean
    onValidationChange?: (hasErrors: boolean) => void
    disabled?: boolean
    action?: any
    dateInputMask?: any
    assignees?: any[]
}

const ReminderManager: React.FC<Props> = ({
    actionSummary,
    dueDate,
    initialReminders,
    initialRememberSettings,
    isLoadingRememberSettings = false,
    onValidationChange,
    disabled = false,
    action,
    dateInputMask,
    assignees = []
}) => {
    const classes = useStyles()
    const form = useForm()
    const [reminderVisible, setReminderVisible] = useState(false)
    const [rememberSettingsVisible, setRememberSettingsVisible] = useState(false)
    const [reminders, setReminders] = useState<Reminder[]>([])
    const [rememberSettings, setRememberSettings] = useState(false)
    const defaultsAppliedRef = React.useRef(false)
    const lastProcessedRef = React.useRef<{ actionId: string | null; dueDate: string | null }>({
        actionId: null,
        dueDate: null
    })
    const [duplicateError, setDuplicateError] = useState<string>('')
    const [valueError, setValueError] = useState<string>('')
    const [reminderErrors, setReminderErrors] = useState<{ [key: number]: boolean }>({})
    const [userChangedFromDueDate, setUserChangedFromDueDate] = useState<boolean>(false)
    const [showPassedDateErrors, setShowPassedDateErrors] = useState<boolean>(false)
    const lastValidatedDueDateRef = React.useRef<string | null>(null)
    const isValidatingDueDateChangeRef = React.useRef<boolean>(false)
    const userInteractedRef = React.useRef<boolean>(false)
    const remindersLengthRef = React.useRef<number>(0)
    const processedDatesRef = React.useRef<string>('')
    const preservedRemindersRef = React.useRef<Reminder[]>([])
    const preservedRememberSettingsRef = React.useRef<boolean>(false)
    const initialAssigneeCountRef = React.useRef<number | null>(null)
    const initialDueDateRef = React.useRef<string | null>(null)

    const dueDateNotSelected = !validDateReminderDate(dueDate)

    const hasAssigneeWithEmail =
        assignees &&
        assignees.length > 0 &&
        assignees.some((assignee) => {
            if (assignee.email && assignee.email.trim() !== '') {
                return true
            }
            if (
                assignee.text &&
                assignee.text.trim() !== '' &&
                /\S+@\S+\.\S+/.test(assignee.text.trim())
            ) {
                return true
            }
            return false
        })

    const reminderDisabled = dueDateNotSelected || !hasAssigneeWithEmail
    const actionId = action?.id

    const calculateReminderDate = (
        dueDateValue: string | Date | null,
        reminderValue: number,
        reminderType: string
    ): Date | null => {
        if (!dueDateValue || !validDateReminderDate(dueDateValue)) {
            return null
        }
        let dueDate: Date
        if (dueDateValue instanceof Date) {
            dueDate = new Date(dueDateValue)
        } else if (typeof dueDateValue === 'string') {
            dueDate = new Date(dueDateValue)
            if (isNaN(dueDate.getTime())) {
                return null
            }
        } else {
            return null
        }
        if (isNaN(dueDate.getTime())) {
            return null
        }
        try {
            const reminderDate = new Date(dueDate)
            switch (reminderType) {
                case 'due_date':
                    return reminderDate
                case 'days':
                    reminderDate.setDate(reminderDate.getDate() - reminderValue)
                    break
                case 'weeks':
                    reminderDate.setDate(reminderDate.getDate() - reminderValue * 7)
                    break
                case 'months':
                    const currentDay = reminderDate.getDate()
                    reminderDate.setMonth(reminderDate.getMonth() - reminderValue)
                    if (reminderDate.getDate() !== currentDay) {
                        reminderDate.setDate(0)
                    }
                    break
                default:
                    return null
            }
            if (isNaN(reminderDate.getTime())) {
                return null
            }
            return reminderDate
        } catch (error) {
            return null
        }
    }

    const isReminderDatePassed = (reminderDate: Date | null): boolean => {
        if (!reminderDate) return false
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const reminderDay = new Date(reminderDate)
        reminderDay.setHours(0, 0, 0, 0)
        return reminderDay < today
    }

    const hasReminderPassedDate = (reminder: Reminder): boolean => {
        if (!dueDate || !showPassedDateErrors || reminder.type === 'due_date') return false

        if (reminderErrors[reminder.id]) return false

        if (valueError !== i18n.t('REMINDER_DATES_PASSED')) return false

        const value = reminder.label
        if (!value || value.trim() === '') return false

        const numValue = parseInt(value, 10)
        if (isNaN(numValue)) return false

        const reminderDate = calculateReminderDate(dueDate, numValue, reminder.type)
        return reminderDate ? isReminderDatePassed(reminderDate) : false
    }

    const onValidationChangeRef = useRef(onValidationChange)
    onValidationChangeRef.current = onValidationChange

    // Notify parent component about validation state changes
    useEffect(() => {
        if (onValidationChangeRef.current) {
            const isWithinYearError = valueError === i18n.t('REMINDER_DATES_WITHIN_A_YEAR')
            const hasIndividualErrors = Object.values(reminderErrors).some((error) => error)

            if (isWithinYearError || hasIndividualErrors) {
                onValidationChangeRef.current(true)
            } else {
                onValidationChangeRef.current(false)
            }
        }
    }, [valueError, duplicateError, reminderErrors])

    // Convert API reminder format to internal format
    const convertAPIToInternalFormat = (
        apiReminders: Array<{ reminder_lead_value: number; reminder_lead_unit: string }>
    ): Reminder[] => {
        return apiReminders.map((apiReminder, index) => ({
            id: Date.now() + index, // Generate unique ID
            label:
                apiReminder.reminder_lead_unit === 'due_date'
                    ? ''
                    : apiReminder.reminder_lead_value.toString(),
            type: apiReminder.reminder_lead_unit
        }))
    }

    // Convert reminders to API format
    const formatRemindersForAPI = (remindersList: Reminder[]) => {
        return remindersList.map((reminder) => ({
            reminder_lead_value: reminder.type === 'due_date' ? 0 : parseInt(reminder.label, 10),
            reminder_lead_unit: reminder.type
        }))
    }

    // Initialize reminders from props when component mounts
    useEffect(() => {
        if (initialReminders && initialReminders.length > 0) {
            const convertedReminders = convertAPIToInternalFormat(initialReminders)
            setReminders(convertedReminders)
            setReminderVisible(true)
            setShowPassedDateErrors(false)
            preservedRemindersRef.current = convertedReminders
        } else {
            setShowPassedDateErrors(true)
        }
    }, [initialReminders])

    // Sync remember checkbox from props when opening a different action or when API value changes.
    // When undefined, default to false so we do not keep the previous action's checked state.
    useEffect(() => {
        if (initialRememberSettings !== undefined) {
            setRememberSettings(!!initialRememberSettings)
            preservedRememberSettingsRef.current = !!initialRememberSettings
        } else {
            setRememberSettings(false)
            preservedRememberSettingsRef.current = false
        }
    }, [initialRememberSettings, actionId])

    // Sync rememberSettings state with form field
    useEffect(() => {
        if (form) {
            form.change('remember_reminder_settings', rememberSettings)
        }
    }, [rememberSettings, form])

    // Check form values for existing reminders data on form initialization
    useEffect(() => {
        if (!form || reminders.length > 0) return

        const timer = setTimeout(() => {
            const formValues = form.getState().values
            if (formValues?.reminders?.length > 0) {
                const convertedReminders = convertAPIToInternalFormat(formValues.reminders)
                setReminders(convertedReminders)
                setReminderVisible(true)
            }
        }, 100)

        return () => clearTimeout(timer) // Clean up timeout on unmount or rerun
    }, [form, reminders])

    const validateAllReminders = useCallback(
        (
            reminderList?: Reminder[],
            options?: { syncRememberCheckboxOnly?: boolean }
        ) => {
            try {
                const remindersToValidate = reminderList || reminders
                let hasError = false
                let hasPassedDateError = false
                const newReminderErrors: { [key: number]: boolean } = {}
                remindersToValidate.forEach((reminder) => {
                    if (reminder.type === 'due_date') {
                        if (dueDate) {
                            const reminderDate = calculateReminderDate(dueDate, 0, 'due_date')
                            if (reminderDate && isReminderDatePassed(reminderDate)) {
                                hasPassedDateError = true
                                newReminderErrors[reminder.id] = false
                            } else {
                                newReminderErrors[reminder.id] = false
                            }
                        } else {
                            newReminderErrors[reminder.id] = false
                        }
                        return
                    }

                    const value = reminder.label
                    let isInvalid = false

                    if (value === '' || value.trim() === '') {
                        isInvalid = true
                        hasError = true
                    }

                    if (value.includes('.') || value.includes(',')) {
                        isInvalid = true
                        hasError = true
                    }

                    if (value.includes('-')) {
                        isInvalid = true
                        hasError = true
                    }

                    const numValue = parseInt(value, 10)

                    if (isNaN(numValue) || numValue.toString() !== value.trim()) {
                        isInvalid = true
                        hasError = true
                    }

                    if (!isInvalid) {
                        let isValidRange = false
                        switch (reminder.type) {
                            case 'days':
                                isValidRange =
                                    numValue >= REMINDER_MIN_VALUE && numValue <= MAX_DAYS
                                break
                            case 'weeks':
                                isValidRange =
                                    numValue >= REMINDER_MIN_VALUE && numValue <= MAX_WEEKS
                                break
                            case 'months':
                                isValidRange =
                                    numValue >= REMINDER_MIN_VALUE && numValue <= MAX_MONTHS
                                break
                            default:
                                isValidRange = true
                        }

                        if (!isValidRange) {
                            isInvalid = true
                            hasError = true
                        }
                        if (isValidRange && dueDate) {
                            const reminderDate = calculateReminderDate(
                                dueDate,
                                numValue,
                                reminder.type
                            )
                            if (reminderDate && isReminderDatePassed(reminderDate)) {
                                hasPassedDateError = true
                            }
                        }
                    }

                    newReminderErrors[reminder.id] = isInvalid
                })

                setReminderErrors(newReminderErrors)

                if (hasError) {
                    setValueError(i18n.t('REMINDER_DATES_WITHIN_A_YEAR'))
                } else if (
                    hasPassedDateError &&
                    showPassedDateErrors &&
                    !options?.syncRememberCheckboxOnly
                ) {
                    setValueError(i18n.t('REMINDER_DATES_PASSED'))
                } else {
                    setValueError('')
                }

                const defaultReminders = getDefaultActionItemReminders()
                let currentRemindersForAPI = formatRemindersForAPI(remindersToValidate)

                let areRemindersDefault = false
                if (defaultReminders.length > 0) {
                    const defaultRemindersForAPI = defaultReminders

                    if (currentRemindersForAPI.length === defaultRemindersForAPI.length) {
                        areRemindersDefault = currentRemindersForAPI.every((current) =>
                            defaultRemindersForAPI.some(
                                (defaultReminder) =>
                                    current.reminder_lead_value ===
                                        defaultReminder.reminder_lead_value &&
                                    current.reminder_lead_unit ===
                                        defaultReminder.reminder_lead_unit
                            )
                        )
                    }
                } else {
                    areRemindersDefault =
                        currentRemindersForAPI.length === 1 &&
                        currentRemindersForAPI[0].reminder_lead_unit === 'due_date' &&
                        currentRemindersForAPI[0].reminder_lead_value === 0
                }

                const shouldShowSettings = !areRemindersDefault || rememberSettings
                setRememberSettingsVisible(shouldShowSettings)
            } catch (error) {
                setReminderErrors({})
                setValueError('')
                setRememberSettingsVisible(false)
            }
        },
        [
            reminders,
            dueDate,
            showPassedDateErrors,
            setReminderErrors,
            setValueError,
            rememberSettings
        ]
    )

    const validateAllRemindersRef = React.useRef(validateAllReminders)
    validateAllRemindersRef.current = validateAllReminders
    const remindersForValidationRef = React.useRef(reminders)
    remindersForValidationRef.current = reminders
    const reminderDisabledForValidationRef = React.useRef(reminderDisabled)
    reminderDisabledForValidationRef.current = reminderDisabled
    const initialRemindersForValidationRef = React.useRef(initialReminders)
    initialRemindersForValidationRef.current = initialReminders

    // getDefaultActionItemReminders() reads transient storage populated by /settings — parent calls refresh in parallel,
    // so first validation often runs too early; settings.ts dispatches this when defaults are stored.
    useEffect(() => {
        if (typeof window === 'undefined') return

        const onSettingsUpdated = () => {
            if (reminderDisabledForValidationRef.current) return
            let list = remindersForValidationRef.current
            if (list.length === 0) {
                const fromProps = initialRemindersForValidationRef.current
                if (fromProps && fromProps.length > 0) {
                    list = convertAPIToInternalFormat(fromProps)
                }
            }
            if (list.length === 0) return
            validateAllRemindersRef.current(list, { syncRememberCheckboxOnly: true })
        }

        window.addEventListener('minutes:settings-updated', onSettingsUpdated)
        return () => window.removeEventListener('minutes:settings-updated', onSettingsUpdated)
    }, [])

    // Opening another action may keep the same due date; due-date-only effect skips when ISO string is unchanged.
    // Sync + layout effect: avoids setTimeout/macrotask lag; paints with correct checkbox visibility.
    useLayoutEffect(() => {
        if (!initialReminders || initialReminders.length === 0 || reminderDisabled) return

        const convertedReminders = convertAPIToInternalFormat(initialReminders)
        validateAllRemindersRef.current(convertedReminders, {
            syncRememberCheckboxOnly: true
        })
    }, [initialReminders, actionId, reminderDisabled])

    // Update form field whenever reminders change
    useEffect(() => {
        if (form) {
            const formattedReminders = formatRemindersForAPI(reminders)
            form.change('reminders', formattedReminders)
        }
        remindersLengthRef.current = reminders.length
    }, [reminders, form])

    useEffect(() => {
        const hasInitialReminders = initialReminders && initialReminders.length > 0
        const assigneeCount = assignees?.length || 0
        const currentDueDateString = dueDate
            ? dueDate instanceof Date && !isNaN(dueDate.getTime())
                ? dueDate.toISOString()
                : String(dueDate)
            : null

        const currentDueDateFormatted = dueDate ? format(dueDate, dateInputMask) : undefined
        const dateComparisonKey = `${currentDueDateFormatted || 'null'}-${
            actionId || 'new'
        }-assignees:${assigneeCount}`

        // Check if user has manually changed due date or assignee count from initial values
        const dueDateChanged =
            initialDueDateRef.current !== null && currentDueDateString !== initialDueDateRef.current
        const assigneeCountChanged =
            initialAssigneeCountRef.current !== null &&
            assigneeCount !== initialAssigneeCountRef.current

        // For new actions (no actionId), auto-populate when both due date and assignee are set
        const isNewAction = !actionId
        const shouldAutoPopulateForNewAction = isNewAction && dueDate && !reminderDisabled

        // For existing actions, only auto-populate when user manually changes due date OR assignee
        const shouldAutoPopulateForExistingAction =
            actionId && (dueDateChanged || assigneeCountChanged) && dueDate && !reminderDisabled

        if (
            (shouldAutoPopulateForNewAction || shouldAutoPopulateForExistingAction) &&
            !disabled &&
            !hasInitialReminders &&
            remindersLengthRef.current === 0 &&
            processedDatesRef.current !== dateComparisonKey
        ) {
            processedDatesRef.current = dateComparisonKey
            userInteractedRef.current = false

            const defaultReminders = getDefaultActionItemReminders()

            if (defaultReminders.length > 0) {
                const convertedReminders = convertDefaultRemindersToInternalFormat(defaultReminders)
                setReminders(convertedReminders)
                setReminderVisible(true)
                defaultsAppliedRef.current = true
                setTimeout(() => {
                    try {
                        if (dueDate && validDateReminderDate(dueDate)) {
                            validateAllReminders(convertedReminders)
                            const dueDateString =
                                dueDate instanceof Date ? dueDate.toISOString() : String(dueDate)
                            if (dueDateString !== 'Invalid Date') {
                                lastValidatedDueDateRef.current = dueDateString
                            }
                        }
                    } catch (error) {}
                }, 0)
            } else {
                const defaultDueDateReminder: Reminder = {
                    id: Date.now(),
                    label: '0',
                    type: 'due_date'
                }
                setReminders([defaultDueDateReminder])
                setReminderVisible(true)
                defaultsAppliedRef.current = true
                setTimeout(() => {
                    try {
                        if (dueDate && validDateReminderDate(dueDate)) {
                            validateAllReminders([defaultDueDateReminder])
                            const dueDateString =
                                dueDate instanceof Date ? dueDate.toISOString() : String(dueDate)
                            if (dueDateString !== 'Invalid Date') {
                                lastValidatedDueDateRef.current = dueDateString
                            }
                        }
                    } catch (error) {}
                }, 0)
            }
        }

        lastProcessedRef.current.dueDate = currentDueDateString
    }, [
        action,
        actionId,
        dueDate,
        disabled,
        reminderDisabled,
        initialReminders,
        validateAllReminders,
        dateInputMask,
        assignees
    ])

    useEffect(() => {
        defaultsAppliedRef.current = false
        userInteractedRef.current = false
        const currentDueDateString = dueDate
            ? dueDate instanceof Date && !isNaN(dueDate.getTime())
                ? dueDate.toISOString()
                : String(dueDate)
            : null
        lastProcessedRef.current = { actionId: null, dueDate: currentDueDateString }

        // Store initial values when action loads
        initialDueDateRef.current = currentDueDateString
        initialAssigneeCountRef.current = assignees?.length || 0
    }, [actionId, dueDate, assignees])

    const isReminderTypeUsed = (type: string, excludeId?: number) => {
        return reminders.some((reminder) => reminder.type === type && reminder.id !== excludeId)
    }

    const checkAndUpdateDuplicateError = (reminderList: Reminder[]) => {
        const typeGroups: { [key: string]: number } = {}
        reminderList.forEach((reminder) => {
            typeGroups[reminder.type] = (typeGroups[reminder.type] || 0) + 1
        })

        const hasDuplicates = Object.values(typeGroups).some((count: number) => count > 1)

        if (hasDuplicates) {
            // setDuplicateError(i18n.t('REMINDER_DUPLICATE_ERROR'))
        } else {
            // setDuplicateError('')
        }
    }

    // Add a new reminder
    const handleAddReminderRow = () => {
        if (reminderDisabled) return
        setReminderVisible(true)

        if (reminders.length < MAX_REMINDERS) {
            const isFirst = reminders.length === 0
            let defaultType = isFirst && !userChangedFromDueDate ? 'due_date' : 'days'

            if (isReminderTypeUsed(defaultType)) {
                const availableTypes = REMINDER_OPTIONS.filter(
                    (option) => !isReminderTypeUsed(option.value)
                )

                if (availableTypes.length > 0) {
                    defaultType = availableTypes[0].value
                } else {
                    // setDuplicateError(i18n.t('REMINDER_DUPLICATE_ERROR'))
                    return
                }
            }

            // Add the new reminder
            const newReminder = {
                id: Date.now(),
                label: defaultType === 'due_date' ? '' : DAYS_DEFAULT_VALUE.toString(),
                type: defaultType
            }

            setReminders([...reminders, newReminder])

            // Check for duplicates and validate values after adding
            setTimeout(() => {
                setReminders((currentReminders) => {
                    checkAndUpdateDuplicateError(currentReminders)
                    validateAllReminders(currentReminders)
                    return currentReminders
                })
            }, 0)
        }
    }

    const handleReminderChange = (id: number, field: string, value: string) => {
        if (field === 'type') {
            const isFirstReminder = reminders.length === 1 && reminders[0].id === id
            if (isFirstReminder && reminders[0].type === 'due_date' && value !== 'due_date') {
                setUserChangedFromDueDate(true)
            }
            // if (isReminderTypeUsed(value, id)) {
            //     setDuplicateError(i18n.t('REMINDER_DUPLICATE_ERROR'))
            // } else {
            //     setDuplicateError('')
            // }
        }

        setReminders((prev) =>
            prev.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value }
                    if (
                        field === 'type' &&
                        item.type === 'due_date' &&
                        (value === 'days' || value === 'weeks' || value === 'months')
                    ) {
                        updatedItem.label = DAYS_DEFAULT_VALUE.toString()
                    }
                    return updatedItem
                }
                return item
            })
        )

        setTimeout(() => {
            setReminders((currentReminders) => {
                checkAndUpdateDuplicateError(currentReminders)
                validateAllReminders(currentReminders)
                return currentReminders
            })
        }, 0)
    }

    // Remove a reminder
    const handleRemoveReminder = (id: number) => {
        const updated = reminders.filter((item) => item.id !== id)
        setReminders(updated)

        setReminderErrors((prev) => {
            const newErrors = { ...prev }
            delete newErrors[id]
            return newErrors
        })

        if (updated.length === 0) {
            setReminderVisible(false)
            setDuplicateError('')
            setValueError('')
            setReminderErrors({})
            setUserChangedFromDueDate(false)
            setShowPassedDateErrors(true)
            setRememberSettings(false)
            return
        }

        setTimeout(() => {
            setReminders((currentReminders) => {
                checkAndUpdateDuplicateError(currentReminders)
                validateAllReminders(currentReminders)
                return currentReminders
            })
        }, 0)
    }

    // Reset reminders when dueDate is cleared or no assignee has email
    useEffect(() => {
        if (reminderDisabled && reminders.length > 0) {
            preservedRemindersRef.current = reminders
            preservedRememberSettingsRef.current = rememberSettings

            setReminders([])
            setDuplicateError('')
            setValueError('')
            setReminderErrors({})
            setRememberSettings(false)
            setUserChangedFromDueDate(false)
            setShowPassedDateErrors(true)
        }
        if (reminderDisabled) {
            setReminderVisible(false)
            processedDatesRef.current = ''
            lastProcessedRef.current.actionId = null
        }

        if (
            !reminderDisabled &&
            reminders.length === 0 &&
            preservedRemindersRef.current.length > 0
        ) {
            setReminders(preservedRemindersRef.current)
            setRememberSettings(preservedRememberSettingsRef.current)
            setReminderVisible(true)

            setTimeout(() => {
                validateAllReminders(preservedRemindersRef.current)
            }, 0)

            preservedRemindersRef.current = []
            preservedRememberSettingsRef.current = false
        }
    }, [reminderDisabled, reminders, rememberSettings, validateAllReminders])

    useEffect(() => {
        if (
            reminders.length > 0 &&
            dueDate &&
            !reminderDisabled &&
            !isValidatingDueDateChangeRef.current
        ) {
            const dueDateString = dueDate instanceof Date ? dueDate.toISOString() : String(dueDate)
            if (dueDateString !== lastValidatedDueDateRef.current) {
                lastValidatedDueDateRef.current = dueDateString
                isValidatingDueDateChangeRef.current = true
                setTimeout(() => {
                    validateAllReminders(reminders)
                    isValidatingDueDateChangeRef.current = false
                }, 0)
            }
            setShowPassedDateErrors(true)
        } else if (reminderDisabled) {
            lastValidatedDueDateRef.current = null
        }
    }, [dueDate, reminderDisabled, reminders, validateAllReminders])

    return (
        <ClassNames>
            {({ css }) => (
                <div
                    className={css`
                        width: 100%;
                        .MuiOutlinedInput-root {
                            margin-top: 4px;
                            padding-top: 4px;
                            height: 40px;
                        }
                    `}>
                    {/* Reminder Label */}
                    {!disabled && (
                        <>
                            <InputLabelStyled
                                className={
                                    actionSummary
                                        ? classes.reminderLabelClassActionDrawer
                                        : classes.reminderLabelClass
                                }
                                htmlFor="reminder-due-date">
                                {translate('REMINDER_LABEL')}
                            </InputLabelStyled>

                            <Box>
                                {!reminderVisible || reminderDisabled ? (
                                    <Box mt={1}>
                                        <InputLabelStyled
                                            style={{
                                                padding: '6px 15px',
                                                marginBottom: actionSummary ? '10px' : '25px',
                                                color:
                                                    reminderDisabled || disabled
                                                        ? 'var(--Semantic-Color-Type-Disabled, #B3B3B3)'
                                                        : 'var(--Semantic-Color-Action-Default, #455D82)',
                                                fontFamily: 'Source Sans Pro',
                                                fontSize: '16px',
                                                fontWeight: 600
                                            }}
                                            disabled={reminderDisabled || disabled}>
                                            <span
                                                onClick={
                                                    disabled ? undefined : handleAddReminderRow
                                                }
                                                style={{
                                                    cursor: disabled ? 'default' : 'pointer'
                                                }}>
                                                {translate('ADD_REMINDER_LABEL')}
                                            </span>
                                        </InputLabelStyled>
                                    </Box>
                                ) : (
                                    <Box>
                                        {/* Render reminder list */}
                                        <div
                                            className={
                                                actionSummary
                                                    ? classes.reminderManageSectionActionDrawer
                                                    : classes.reminderManageSection
                                            }>
                                            <div className={classes.reminderMessage}>
                                                {translate('REMINDER_MESSAGE')}
                                            </div>

                                            {reminders.map((item) => (
                                                <Grid
                                                    container
                                                    spacing={1}
                                                    alignItems="center"
                                                    key={item.id}
                                                    style={{ marginBottom: 4, minHeight: 40 }}>
                                                    {/* Reminder value (if not on due date) */}
                                                    {item.type !== 'due_date' && (
                                                        <Grid
                                                            className={`${
                                                                actionSummary
                                                                    ? classes.reminderRowValueActionDrawer
                                                                    : classes.reminderRowValue
                                                            }`}
                                                            item>
                                                            <NewDialogTextField
                                                                name={`reminder_lead_value_${item.id}`}
                                                                className={`${classes.noSpinner} ${
                                                                    classes.textField
                                                                } ${classes.rightAlignInput} ${
                                                                    reminderErrors[item.id]
                                                                        ? classes.errorInput
                                                                        : hasReminderPassedDate(
                                                                              item
                                                                          )
                                                                        ? classes.warningInput
                                                                        : ''
                                                                } ${
                                                                    disabled
                                                                        ? classes.disabledInput
                                                                        : ''
                                                                }`}
                                                                variant="outlined"
                                                                type="number"
                                                                value={item.label}
                                                                onChange={(e) => {
                                                                    handleReminderChange(
                                                                        item.id,
                                                                        'label',
                                                                        e.target.value
                                                                    )
                                                                }}
                                                                disabled={
                                                                    disabled ||
                                                                    item.type === 'due_date'
                                                                }
                                                                InputProps={{
                                                                    style: { height: 40 }
                                                                }}
                                                            />
                                                        </Grid>
                                                    )}

                                                    {/* Reminder unit (dropdown) */}
                                                    <Grid
                                                        className={
                                                            item.type !== 'due_date'
                                                                ? actionSummary
                                                                    ? classes.reminderRowUnitActionDrawer
                                                                    : classes.reminderRowUnit
                                                                : actionSummary
                                                                ? classes.reminderRowUnitOnDueDateActionDrawer
                                                                : classes.reminderRowUnitOnDueDate
                                                        }
                                                        item>
                                                        <NewDialogTextField
                                                            select
                                                            variant="outlined"
                                                            className={
                                                                disabled
                                                                    ? classes.disabledInput
                                                                    : ''
                                                            }
                                                            name={`reminder_lead_unit_${item.id}`}
                                                            value={item.type}
                                                            onChange={(e) =>
                                                                handleReminderChange(
                                                                    item.id,
                                                                    'type',
                                                                    e.target.value
                                                                )
                                                            }
                                                            disabled={disabled}
                                                            InputProps={{ style: { height: 40 } }}>
                                                            {REMINDER_OPTIONS.filter((opt) => {
                                                                if (opt.value === 'due_date') {
                                                                    return !reminders.some(
                                                                        (reminder) =>
                                                                            reminder.type ===
                                                                                'due_date' &&
                                                                            reminder.id !== item.id
                                                                    )
                                                                }
                                                                return true
                                                            }).map((opt) => (
                                                                <MenuItem
                                                                    key={opt.value}
                                                                    value={opt.value}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </NewDialogTextField>
                                                    </Grid>

                                                    {/* Remove reminder */}
                                                    <Grid
                                                        className={classes.reminderRowUnitClose}
                                                        item>
                                                        <IconButton
                                                            onClick={() =>
                                                                handleRemoveReminder(item.id)
                                                            }
                                                            disabled={disabled}>
                                                            <AtlasIcon name="close" size={24} />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            ))}

                                            {/* Add Additional Reminder */}
                                            {reminders.length < MAX_REMINDERS && (
                                                <Box mt={1}>
                                                    <Button
                                                        onClick={handleAddReminderRow}
                                                        className={classes.reminderAdditionalButton}
                                                        style={{ textTransform: 'none' }}
                                                        disabled={disabled}
                                                        disableFocusRipple
                                                        disableRipple>
                                                        {translate('ADDITIONAL_REMINDER_LABEL')}
                                                    </Button>
                                                </Box>
                                            )}

                                            {valueError === i18n.t('REMINDER_DATES_PASSED') && (
                                                <span className={classes.warningMessage}>
                                                    <RestrictedWarningIcon />
                                                    <Typography className={classes.warningMessage}>
                                                        {translate('REMINDER_DATES_PASSED')}
                                                    </Typography>
                                                </span>
                                            )}

                                            {valueError !== i18n.t('REMINDER_DATES_PASSED') && (
                                                <span className={classes.errorMessage}>
                                                    <TextLengthCounter
                                                        errorText={valueError || duplicateError}
                                                        value={''}
                                                        maxValue={0}
                                                        colour={!!(valueError || duplicateError)}
                                                    />
                                                </span>
                                            )}
                                        </div>
                                    </Box>
                                )}
                            </Box>

                            
                            {(rememberSettingsVisible || isLoadingRememberSettings) &&
                                reminderVisible &&
                                !reminderDisabled &&
                                reminders.length > 0 && (
                                    <div
                                        className={`${classes.textFieldContainer} ${
                                            actionSummary
                                                ? classes.textfiledcontainerActionDrawer
                                                : ''
                                        }`}>
                                        {isLoadingRememberSettings ? (
                                            <div
                                                className={classes.checkBoxContainer}
                                                style={{ padding: '4px 0' }}>
                                                <CircularProgress size={20} />
                                                <Typography
                                                    className={classes.checkboxTitle}
                                                    style={{
                                                        marginLeft: '12px',
                                                        color: 'rgba(0, 0, 0, 0.38)'
                                                    }}>
                                                    {translate('REMINDER_MY_SETTINGS_LABEL')}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Field
                                                name="remember_reminder_settings"
                                                subscription={{ value: true, error: true }}
                                                render={({ input }) => {
                                                    const checked = rememberSettings

                                                    const handleChange = (newValue) => {
                                                        setRememberSettings(newValue)
                                                        input.onChange(newValue)
                                                    }

                                                    return (
                                                        <div
                                                            className={
                                                                actionSummary
                                                                    ? classes.textFieldContainerActionDrawer
                                                                    : classes.textFieldContainer
                                                            }>
                                                            <span
                                                                className={
                                                                    classes.checkBoxContainer
                                                                }>
                                                                <Checkbox
                                                                    disableRipple
                                                                    className={classes.checkbox}
                                                                    checked={checked}
                                                                    onChange={(e) => {
                                                                        handleChange(
                                                                            e.target.checked
                                                                        )
                                                                    }}
                                                                    disabled={disabled}
                                                                    color="default"
                                                                    inputProps={
                                                                        {
                                                                            'data-analytics': checked
                                                                                ? 'MM-Reminder-RememberMySettingsEnable'
                                                                                : 'MM-Reminder-RememberMySettingsDisable'
                                                                        } as React.InputHTMLAttributes<
                                                                            HTMLInputElement
                                                                        >
                                                                    }
                                                                />
                                                                <Typography
                                                                    className={
                                                                        classes.checkboxTitle
                                                                    }
                                                                    onClick={() => {
                                                                        if (!disabled) {
                                                                            handleChange(!checked)
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        cursor: disabled
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        color: disabled
                                                                            ? 'rgba(0, 0, 0, 0.38)'
                                                                            : undefined
                                                                    }}>
                                                                    {translate(
                                                                        'REMINDER_MY_SETTINGS_LABEL'
                                                                    )}
                                                                </Typography>
                                                            </span>
                                                        </div>
                                                    )
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                        </>
                    )}

                    {/* Hidden field to store reminders array for form submission */}
                    <Field name="reminders" render={() => null} />
                </div>
            )}
        </ClassNames>
    )
}

export default ReminderManager
